// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — push observers: selection diff + document-changed swap
//                        monitor + transactional audit emit + demotion gating
//                        (ABB-S5b/S5c/S5d)
// ═══════════════════════════════════════════════════════════════════════════
//
// The browser's outbound STATE-CHANGE emitters, wired into the live store by S7
// via DI. Three emitters + a demotion gate, ALL gated on "I am the current ACTIVE
// epoch" (an observer / demoted / never-paired tab emits NOTHING, SM-15):
//
//   (b) SELECTION-PUSH (SM-6) — a `store.subscribe` diff of
//       `state.selection.entityIds` vs the LAST-PUSHED set, on an INDEPENDENT
//       high-water from the audit range (a selection mutation must NOT be swallowed
//       by — nor swallow — the audit-turn range). On a real membership change →
//       `push{event:'selection', payload:{entityIds:[…full set…]}}`.
//
//   (audit) TRANSACTIONAL AUDIT-PUSH (SM-5) — NOT a passive subscribe (that would
//       flush mid-turn + push the abort-inverse noise). The command.run turn reads
//       the audit high-water before/after the synchronous contour and projects the
//       `[pre, post)` events into a FROZEN snapshot SYNCHRONOUSLY at commit (BEFORE
//       the batchHash await, P2-2 — the ring's 24-cap means a re-projection after the
//       await could be evicted). On COMMIT S7 hands that snapshot to
//       `emitTurnAuditOnCommit(auditEvents)` here → push it verbatim, STAMPED with the
//       CURRENT welcome epoch (round-4 — read at the synchronous emit instant). The
//       command.run handler fires this SYNCHRONOUSLY at commit (before its hash await),
//       so at the emit instant `getCurrentEpoch() === the command's dispatch epoch` (no
//       soft re-key can have interleaved): the stamp is BOTH correctly segmented (the
//       turn's own epoch the rpc-result settles on) AND companion-accepted
//       (`brokerReadFace.onBrowserPush` drops any push whose `sessionEpoch !==
//       currentQuad().sessionEpoch`; WS-FIFO delivers this push before any same-tab
//       `document-changed` re-key). The round-3 captured-dispatch-epoch OVERRIDE is GONE
//       — it only existed for a DEFERRED emission, which is exactly what the companion
//       gate rejects (the audit becomes the SAME current-epoch signal a selection push
//       is). Same set as the rpc-result `auditRefs` (the P2-1 off-wire ≡ refs invariant).
//       On ABORT S7 calls NOTHING (the range is discarded, INV-AUDIT-TX).
//
//   (d) DOCUMENT-CHANGED swap monitor (SM-16) — a CONTROL frame (NOT a push) on a
//       TRUE document SWAP. Discriminated by the monotonic `documentEpoch`, NOT the
//       documentId alone: a true swap is `(documentId changed) OR (documentId same
//       AND documentEpoch INCREASED)`. The file-OPEN swap (worker mint + jsdom-
//       fallback mint converge at `editorStore.ts` ~6280) mints a fresh id AND
//       `mintNewDocumentEpoch` — caught by the id-change arm. But `reset()` /
//       `New` (`editorStore.ts` ~7142) is ALSO a true swap that calls
//       `mintNewDocumentEpoch` while KEEPING the FIXED id `"dwg-stage-a-geometry"`
//       (`buildInitialGeometryDocument`) — an id-ONLY predicate MISSES it, so the
//       companion never soft-re-keys and the agent works against a stale revision
//       base. The epoch arm catches it: `documentEpoch` is monotonic per loaded
//       document and is bumped ONLY by `mintNewDocumentEpoch` (a true swap) —
//       `bumpForClasses` (the INSERT/import RE-STAMP + every committed mutation)
//       does NOT bump it (`revisionModel.ts`), so an epoch INCREASE ⇔ a true swap
//       with NO re-stamp false-positive. (id same AND epoch same) is INERT — a
//       re-stamp / HMR / ordinary mutation tick → NO soft-re-key loop. On a swap
//       the monitor emits `document-changed` (the client then accepts the
//       companion's unsolicited welcome{epoch+1}, SM-12).
//
//   DEMOTION gate — on the store tick the monitor re-checks
//       `sink.isCurrentActiveEpoch()`; on a demotion (active→not) it STOPS all
//       pushes AND fires `sink.onDemotion()` so S4's in-flight turn self-aborts
//       (`superseded-by-takeover`, the S4 pre-commit self-check, SM-15). The store
//       tick is NOT the only demotion trigger: a `session-changed` (or unsolicited
//       superseding welcome) role-change frame flips `isCurrentActiveEpoch()` with
//       NO simultaneous store mutation, so the SESSION/EPOCH role-change path MUST
//       drive the demotion check too — `checkDemotionNow()` is the seam S7 wires
//       into the client's `onRoleChange` callback (the same place
//       `SessionTracker.isCurrentActiveEpoch` flips) so an active→observer edge
//       delivered purely on the wire fires `onDemotion()` IMMEDIATELY, independent
//       of any store tick. The store subscriber keeps its own check (belt-and-
//       braces) — the edge-once high-water (`wasActive`) makes the two paths
//       idempotent (whichever observes the edge first fires the single abort).
//
// DEP-GRAPH (ПРИОРИТЕТ #2): a LEAF factory — imports ONLY the `auditStaging`
// sibling (a leaf) + types. NO editorStore (the store coupling is the injected
// `subscribe`/`getSnapshot` seam, mirroring S3's DI), NO contracts, NO socket I/O
// (the wire coupling is the injected `PushSink`). `npm run deps:graph` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import {
  BRIDGE_AUDIT_ACTOR_EMAIL,
  type StagedAuditEntry,
} from "./auditStaging";

/**
 * A reference- AND value-distinct sentinel the post-settle re-key path parks in
 * `lastPushedSelection` so the subsequent `checkSelectionNow` membership diff is
 * GUARANTEED to differ from the current selection (even an empty `[]`), forcing a
 * re-push under the freshly-settled document. No real entity id can collide with it
 * (the `\u0000` prefix is not a valid entity id), so it never suppresses a genuine
 * same-membership selection.
 */
const RESETTLE_SENTINEL = "\u0000agent-bridge:resettle-sentinel";

/**
 * The MINIMAL live-store snapshot the observers read (a STRUCTURAL subset of the
 * editorStore — the module never imports editorStore; S7 supplies a `getSnapshot`
 * that reads the real store). `selectionEntityIds` is the current selection
 * membership; `documentId` / `documentEpoch` are the live document identity (for
 * the swap monitor); `auditRingNewestFirst` is the audit-event ring (newest-first,
 * the shape `reducerHelpers.appendAuditEvent` prepends) read at audit-push time.
 */
export interface PushObserverSnapshot {
  readonly selectionEntityIds: readonly string[];
  readonly documentId: string | null;
  readonly documentEpoch: number | null;
  readonly auditRingNewestFirst: readonly StagedAuditEntry[];
}

/**
 * The store-subscription seam (S7 wires zustand's `subscribe` + `getState`-derived
 * snapshot). `subscribe` returns an UNSUBSCRIBE thunk (zustand contract). The
 * observer never imports editorStore — this is the only store coupling.
 */
export interface PushObserverStore {
  /** Read the current minimal snapshot (S7: derive from `useEditorStore.getState()`). */
  getSnapshot: () => PushObserverSnapshot;
  /** Subscribe to store changes; returns the unsubscribe thunk (zustand). */
  subscribe: (listener: () => void) => () => void;
}

/**
 * The WIRE + AUTHORITY sink the observers drive (S7 wires the live
 * `AgentBridgeClient`). Kept a MINIMAL structural interface (not the whole client)
 * so the module stays a testable leaf with a mock sink.
 */
export interface PushSink {
  /** Whether THIS tab is the current ACTIVE epoch (the demotion / push gate, SM-15). */
  isCurrentActiveEpoch: () => boolean;
  /**
   * Send a STATE-CHANGE push frame (the client gates welcomed + open socket + active).
   *
   * round-4 — BOTH the AUDIT and the SELECTION push stamp the client's CURRENT welcome
   * epoch (`agentBridgeClient.sendPush` → `getCurrentEpoch()`); there is NO per-push
   * epoch override. The audit push is correct under the current epoch because the
   * command.run handler fires `emitTurnAuditOnCommit` SYNCHRONOUSLY at commit (before its
   * hash await): at that instant the current epoch still equals the command's dispatch
   * epoch (no soft re-key has interleaved), so the stamp is BOTH correctly segmented (the
   * turn's own epoch the rpc-result settles on) AND companion-accepted
   * (`brokerReadFace.onBrowserPush` drops any push whose `sessionEpoch !==
   * currentQuad().sessionEpoch` — the round-3 captured-old-epoch override was exactly what
   * that gate rejected after a re-key). WS-FIFO delivers this push before any same-tab
   * `document-changed` re-key frame. The client gates welcomed + active + null-epoch.
   */
  sendPush: (event: "audit" | "selection", payload: unknown) => void;
  /** Send a `document-changed` CONTROL frame on a true swap (the client gates welcomed). */
  sendDocumentChanged: (documentId: string | null, documentEpoch: number | null) => void;
  /**
   * Fire S4's in-flight self-abort on a detected demotion (the SAME path the S4
   * pre-commit self-check fires — `superseded-by-takeover`). The client wires it to
   * `onAbortInProgressTurn`. Idempotent (it no-ops when no turn is in flight).
   */
  onDemotion: () => void;
}

/** A running push-observer handle — `stop()` unsubscribes (S7 teardown / HMR dispose). */
export interface PushObserverHandle {
  /**
   * Emit the TRANSACTIONAL audit push for a COMMITTED command.run turn (SM-5). S7
   * calls this ONLY on a successful turn. GATED on the active epoch — a turn that
   * committed while demoted emits nothing. Pushes nothing for an empty snapshot (a
   * no-op turn).
   *
   * P2-2 — S7 passes the `auditEvents` SNAPSHOT the S4 handler projected
   * SYNCHRONOUSLY at commit time (BEFORE the batchHash await), NOT the post-await
   * pre/post high-water: the ring's 24-cap means browser audit activity during the
   * handler's `await computeBatchHash` could EVICT this turn's committed entries, so
   * a re-projection of the LIVE ring here would push an empty/partial set even though
   * the turn committed. Using the handler's frozen snapshot makes the push immune to
   * post-await eviction AND identical to the rpc-result `auditRefs` (both derived from
   * the SAME snapshot — the P2-1 invariant). The snapshot is already projected +
   * ascending + cap-bounded (the surviving ring entries in `[pre, post)`), so this
   * does not re-project; it only gates + tags + sends.
   *
   * round-4 EPOCH — the audit push is STAMPED with the CURRENT welcome epoch (read by
   * the client's `sendPush` → `getCurrentEpoch()`), the SAME as a selection push; there
   * is NO captured-dispatch-epoch override. The SYNCHRONOUS-EMIT CONTRACT makes this
   * correct: the S4 handler invokes `onTurnAuditCommitted` (→ this) SYNCHRONOUSLY at
   * commit, BEFORE its hash await, so NO soft re-key can have interleaved — the current
   * epoch at this instant equals the command's dispatch epoch (the epoch the rpc-result
   * settles on via the minted-scope history). A current-epoch stamp is therefore BOTH
   * correctly segmented AND companion-accepted (`brokerReadFace.onBrowserPush` drops any
   * push whose `sessionEpoch !== currentQuad().sessionEpoch` — the round-3 override was
   * exactly what that gate rejected after a re-key). ⚠ A DEFERRED call (after the await)
   * would BREAK this: the current epoch could have advanced (mis-segment) AND the
   * companion would have re-keyed (drop). The handler emits this synchronously by
   * construction (no deferred callback hop). The null-guard below skips the push when the
   * tab is unpaired (`getCurrentEpoch() === null` — a committed turn was paired, so this
   * is defensive). `sessionEpoch` is DISTINCT from `documentEpoch`.
   */
  emitTurnAuditOnCommit: (auditEvents: readonly StagedAuditEntry[]) => void;
  /** Force a selection-diff re-check (test helper / explicit flush). */
  checkSelectionNow: () => void;
  /** Force a document-swap re-check (test helper / explicit flush). */
  checkDocumentSwapNow: () => void;
  /**
   * Force a demotion re-check driven by the SESSION/EPOCH role-change path (SM-15).
   * S7 wires the `AgentBridgeClient.onRoleChange` callback (the same place
   * `SessionTracker.isCurrentActiveEpoch` flips on a `session-changed` /
   * superseding-welcome frame) to call THIS — so an active→observer demotion
   * delivered purely on the wire (NO simultaneous store mutation) fires
   * `sink.onDemotion()` IMMEDIATELY, not deferred to an unrelated store tick. Shares
   * the same `wasActive` edge high-water as the store-subscriber check, so whichever
   * path observes the active→not edge first fires the single abort; the other is a
   * no-op (idempotent).
   */
  checkDemotionNow: () => void;
  /**
   * Flush the PENDING selection UNDER THE SETTLED document belief (@codex P2 —
   * the welcome-ordering fix). S7 wires this into the client's `onDocumentChanged`
   * adoption callback (the POST-re-key hook — `agentBridgeClient.onWelcome` fires it
   * AFTER `setStatus(active)` AND after `refreshDocumentIdentityIfStale` sends the
   * stale-on-promotion `document-changed`, so by WS-FIFO the re-key frame already
   * left the socket before this flush's selection push). It does TWO things:
   *
   *   1. If the settled document IDENTITY differs from the one the last selection
   *      push was emitted under (a re-key / soft swap), it RESETS the selection
   *      high-water so the post-settle flush RE-PUSHES the current selection under
   *      the NEW document — even when the membership is byte-identical to the
   *      pre-re-key push (the companion has NO selection for the re-keyed document
   *      until we re-assert it). This mirrors `checkDocumentSwapNow`'s local-swap
   *      high-water reset (the companion-driven re-key is the symmetric inbound half).
   *   2. It then runs `checkSelectionNow()` — flushing the pending selection EXACTLY
   *      ONCE under the now-settled document (role/pairing/membership-gated as usual,
   *      so an observer / unpaired / unchanged tick is a harmless no-op).
   *
   * WHY HERE and NOT in `onRoleChange`: `SessionTracker.adoptWelcome` fires
   * `onRoleChange` BEFORE `onWelcome` runs `refreshDocumentIdentityIfStale`, so an
   * `onRoleChange` flush would push the selection under the OLD (stale) companion
   * document; the subsequent `document-changed` re-key would then re-key WITHOUT the
   * selection (the companion consumed the diff under the old document, and the
   * high-water was already advanced so nothing re-pushes). Flushing from the
   * post-settle `onDocumentChanged` path pushes once, under the settled identity, for
   * BOTH the no-doc-change become-active case (no re-key sent, belief unchanged → a
   * plain flush) AND the stale-doc re-key case (re-key sent first → reset + re-push).
   * An idempotent re-flush is a no-op (the membership already matches the advanced
   * high-water, keyed to the unchanged LIVE local document). A teardown belief (all-null)
   * is NOT routed here (S7 calls `documentEpochCache.reset()` for that branch, never this
   * flush). The re-push decision keys on the LIVE LOCAL document (read internally), NOT the
   * companion belief, which lags across a soft re-key — so this takes no belief argument.
   */
  flushSelectionForSettledDocument: () => void;
  /** Stop observing (unsubscribe). Idempotent. */
  stop: () => void;
}

/**
 * Whether two id sets have the SAME DE-DUPLICATED membership (order-insensitive).
 *
 * P3 (round-3) — compare the DE-DUPED SET membership in BOTH directions, NOT
 * `length-equal && current ⊆ last`. The old one-way length+subset test mishandled
 * DUPLICATE ids: `current=["a","a"]` vs `last=["a","b"]` has equal length AND every
 * `current` id is in `last`, so it wrongly reported "same membership" → NO selection
 * push, even though the de-duped sets `{a}` vs `{a,b}` DIFFER (the selection actually
 * changed). Symmetric set equality (`size equal AND every member of A is in B`, which
 * with equal sizes also implies B ⊆ A) is correct regardless of duplicates and order:
 * `{a}` ≠ `{a,b}` → emits; `["a","b"]` vs `["b","a"]` → `{a,b}` === `{a,b}` → no push.
 */
function sameMembership(a: readonly string[], b: readonly string[]): boolean {
  const aSet = new Set(a);
  const bSet = new Set(b);
  if (aSet.size !== bSet.size) {
    return false;
  }
  for (const id of aSet) {
    if (!bSet.has(id)) {
      return false;
    }
  }
  return true;
}

/**
 * Create + START the push observers, subscribing to the store. Returns a handle
 * whose `stop()` unsubscribes. INV-OFF: this is a FACTORY — S7 INSTANTIATES it
 * (and only when the client is connected active); merely importing the module
 * subscribes to nothing.
 *
 * `getCurrentEpoch` is a NULL-GUARD only — the observers read it to confirm the
 * tab is paired (epoch !== null) before driving the sink; an unpaired tab emits
 * nothing. It is NOT the value stamped on the wire: the CLIENT
 * (`AgentBridgeClient.sendPush`) reads its OWN authoritative session epoch and
 * stamps the outbound frame (`buildPushFrame`), so the frame always carries the
 * epoch the companion epoch-drops against (SM-5/SM-6) even if these two reads were
 * ever to skew. This is the `sessionEpoch` axis — DISTINCT from the document-swap
 * monitor's `documentEpoch` (the per-loaded-document swap signal).
 */
export function createPushObservers(deps: {
  store: PushObserverStore;
  sink: PushSink;
  /** Pairing NULL-GUARD (the client stamps the wire epoch; this only gates unpaired). */
  getCurrentEpoch: () => number | null;
}): PushObserverHandle {
  const { store, sink, getCurrentEpoch } = deps;

  // ── selection high-water (INDEPENDENT of the audit range, SM-6) ─────────────
  // [@codex D] SEED the high-water to the SENTINEL — NOT the live snapshot's
  // `selectionEntityIds`. The initial selection is PENDING until the FIRST post-welcome
  // flush; seeding it to the current membership would make that first `checkSelectionNow`
  // see NO diff (current === seed) and return early → the companion never learns the
  // selection the document opened with until it CHANGES again. The sentinel guarantees the
  // first membership diff differs (even an empty `[]` selection), so the first sendable
  // tick / post-welcome flush asserts the current selection EXACTLY ONCE.
  let lastPushedSelection: readonly string[] = [RESETTLE_SENTINEL];

  // ── LIVE local document the last selection push was emitted under (@codex P2) ──
  // Keyed on the LIVE LOCAL document (NOT the companion's lagging welcome belief — which
  // oscillates stale→fresh across a soft re-key and would double-push). A post-settle
  // flush re-pushes the current selection ONLY when the live local document differs from
  // the one the last push was asserted under — so a same-membership-across-swap selection
  // is re-asserted under the new document EXACTLY ONCE, and a re-key echo welcome (same
  // live document) is a no-op. Seeded from the live snapshot.
  let lastPushedSelectionDocumentId: string | null = store.getSnapshot().documentId;
  let lastPushedSelectionDocumentEpoch: number | null = store.getSnapshot().documentEpoch;

  // ── document-swap high-water (the swap discriminator, SM-16) ────────────────
  // Track BOTH the id AND the monotonic documentEpoch: a true swap is an id change
  // OR a same-id epoch INCREASE (reset()/New keeps the FIXED id but mints a new
  // epoch — an id-only predicate would miss it). See the (d) header.
  let lastEmittedDocumentId: string | null = store.getSnapshot().documentId;
  let lastEmittedDocumentEpoch: number | null = store.getSnapshot().documentEpoch;

  // ── demotion high-water (fire onDemotion exactly once per active→not edge) ───
  let wasActive = sink.isCurrentActiveEpoch();

  // ── re-key-pending flag (@codex E — selection lands under the SETTLED doc) ───
  // SET when `checkDocumentSwapNow` emits a `document-changed` for a LOCAL swap; CLEARED in
  // `flushSelectionForSettledDocument` after the post-settle flush. While SET, a selection
  // change is NOT pushed (it would land under the UNSETTLED document — the companion has not
  // adopted the re-key yet). `checkSelectionNow` returns early WITHOUT advancing the
  // high-water while this is set, so the change stays PENDING; the welcome-driven
  // `onDocumentChanged` post-settle flush (which fires AFTER the WS-FIFO re-key frame) then
  // pushes it EXACTLY ONCE under the settled document. NET: the selection lands once, under
  // the settled doc — never premature (mid-re-key), never stranded.
  let reKeyPending = false;

  function checkSelectionNow(): void {
    // GATE: an observer / demoted tab emits no selection push (SM-15). Return
    // WITHOUT advancing the high-water — the next sendable tick (after promotion)
    // must still see this as a pending change so it emits the current selection.
    if (!sink.isCurrentActiveEpoch()) {
      return;
    }
    // [@codex E] RE-KEY-PENDING gate: a LOCAL document swap sent a `document-changed` that
    // the companion has NOT yet adopted (no re-key welcome back). A selection push emitted
    // now would land UNDER THE UNSETTLED document (the companion still keys the old doc) —
    // so leave the change PENDING (return WITHOUT advancing the high-water). The post-settle
    // `flushSelectionForSettledDocument` (welcome-driven, AFTER the WS-FIFO re-key frame)
    // clears this flag + flushes the pending selection exactly once under the settled doc.
    if (reKeyPending) {
      return; // a re-key is in flight → not settled → leave the selection UNADVANCED.
    }
    const current = store.getSnapshot().selectionEntityIds;
    if (sameMembership(current, lastPushedSelection)) {
      return; // no membership change → no spurious push.
    }
    // P2-3 — NULL-GUARD an unpaired / reconnecting tab BEFORE advancing the
    // high-water: a selection change while `getCurrentEpoch() === null` could not be
    // sent, so it must NOT advance `lastPushedSelection`. If it did, then after the
    // next welcome the current selection would equal the saved high-water → the
    // membership-diff returns early → NO full-selection push ever fires → the
    // companion never learns the selection until it changes AGAIN. Returning here
    // WITHOUT advancing leaves the change pending so the next sendable tick (a
    // welcome-driven recheck, or the next real selection mutation) emits the current
    // full selection. The CLIENT stamps the authoritative wire epoch on the frame
    // (`AgentBridgeClient.sendPush`); this is only the pairing null-guard.
    if (getCurrentEpoch() === null) {
      return; // not paired → not sendable → leave the high-water UNADVANCED.
    }
    // Sendable: snapshot the FULL current membership (a defensive copy — the store's
    // array ref may be reused) and advance the independent high-water — only AFTER a
    // push is actually emitted, so a non-sendable tick never strands the diff.
    const snap = store.getSnapshot();
    const snapshotIds = [...current];
    lastPushedSelection = snapshotIds;
    // Record the LIVE local document this selection was asserted under (@codex P2) so a
    // later companion-driven re-key (post-settle flush) re-pushes the selection under a
    // CHANGED live document — even if the membership is unchanged — exactly once.
    lastPushedSelectionDocumentId = snap.documentId;
    lastPushedSelectionDocumentEpoch = snap.documentEpoch;
    sink.sendPush("selection", { entityIds: snapshotIds });
  }

  function checkDocumentSwapNow(): void {
    const snap = store.getSnapshot();
    const currentId = snap.documentId;
    const currentEpoch = snap.documentEpoch;
    const idChanged = currentId !== lastEmittedDocumentId;
    // A same-id epoch INCREASE is a true swap (reset()/New keep the FIXED id but
    // mint a new documentEpoch via `mintNewDocumentEpoch`; `bumpForClasses` — the
    // re-stamp / ordinary-mutation path — never bumps the epoch). Use STRICT `>`
    // (monotonic) so a same / stale / null epoch on the same id is NOT a swap.
    const epochIncreasedSameId =
      !idChanged &&
      typeof currentEpoch === "number" &&
      typeof lastEmittedDocumentEpoch === "number" &&
      currentEpoch > lastEmittedDocumentEpoch;
    if (!idChanged && !epochIncreasedSameId) {
      return; // SAME id + SAME epoch → a RE-STAMP / HMR / mutation tick, NOT a swap.
    }
    // A TRUE swap: the documentId changed (file-OPEN mints a fresh id + epoch) OR a
    // same-id epoch increase (reset()/New keep the fixed id but mint a new epoch).
    // Advance BOTH high-waters BEFORE the send so a re-entrant tick cannot double-emit.
    lastEmittedDocumentId = currentId;
    lastEmittedDocumentEpoch = currentEpoch;
    // Reset the selection high-water on a swap — the new document's selection is a
    // fresh baseline (the swap clears selection; a stale lastPushed would suppress
    // the first real selection of the new doc or push a phantom diff).
    lastPushedSelection = snap.selectionEntityIds;
    // [@codex E] MARK a re-key as in flight: the companion has not yet adopted this swap
    // (it re-keys + welcomes back). Until the welcome-driven post-settle flush runs, a
    // selection push must NOT leave (it would land under the unsettled document). The
    // post-settle `flushSelectionForSettledDocument` clears this after it flushes.
    reKeyPending = true;
    // document-changed is a CONTROL frame sent regardless of active/observer role:
    // the client's `sendDocumentChanged` is the project-out half + gates on
    // `welcomed`; a swap in an observer tab still re-keys correctly when promoted
    // (the client's refreshDocumentIdentityIfStale re-asserts on promotion). We
    // still only emit on a real id change.
    sink.sendDocumentChanged(currentId, snap.documentEpoch);
  }

  function checkDemotion(): void {
    const active = sink.isCurrentActiveEpoch();
    if (wasActive && !active) {
      // active → demoted edge: stop pushes (the per-emit gate already returns) +
      // fire the S4 in-flight self-abort (`superseded-by-takeover`, SM-15).
      sink.onDemotion();
    }
    wasActive = active;
  }

  // ── stopped flag (declared BEFORE the tick so the guard below can read it) ──
  let stopped = false;

  // The store listener runs all three observers on every tick (cheap: each is an
  // O(selection-size) diff / id compare; the store already batches its sets).
  function onStoreTick(): void {
    // [sibling P3] GUARD against a tick delivered AFTER `stop()`: zustand's `subscribe`
    // returns an unsubscribe thunk, but a store mutation can fire a queued listener between
    // `stop()` setting the flag and the unsubscribe actually detaching — a final spurious
    // tick would otherwise drive a push / abort onto a torn-down sink. Bail out fail-closed.
    if (stopped) {
      return;
    }
    checkDemotion();
    checkDocumentSwapNow();
    checkSelectionNow();
  }

  const unsubscribe = store.subscribe(onStoreTick);

  function emitTurnAuditOnCommit(auditEvents: readonly StagedAuditEntry[]): void {
    // GATE: a turn that committed while demoted emits nothing (SM-15).
    if (!sink.isCurrentActiveEpoch()) {
      return;
    }
    // round-4 — NULL-GUARD on pairing (`getCurrentEpoch()`, the SAME guard the selection
    // path uses): an unpaired tab cannot stamp a wire epoch, so it emits nothing. This is
    // defensive — a committed turn was necessarily paired. The audit push is then stamped
    // with the CURRENT welcome epoch by the client's `sendPush` (no override): the S4
    // handler fires this SYNCHRONOUSLY at commit (before its hash await), so at this
    // instant the current epoch still equals the command's dispatch epoch — correctly
    // segmented AND companion-accepted (see the contract doc above). A DEFERRED emission
    // would break both; the handler emits synchronously by construction.
    if (getCurrentEpoch() === null) {
      return; // not paired → not stampable → nothing to push.
    }
    // round-4 — push the handler's FROZEN pre-await snapshot verbatim (no re-projection
    // of the live ring, which could be cap-evicted during the handler's hash await).
    // Defensive copy so a later mutation of the caller's array cannot mutate the
    // payload. The snapshot is already projected, ascending, and cap-bounded.
    const events = [...auditEvents];
    if (events.length === 0) {
      return; // an empty / no-op turn appended nothing → no audit push.
    }
    // The client stamps the CURRENT welcome epoch (the same as a selection push); the
    // synchronous-emit contract makes current === dispatch here. The client also gates
    // welcomed + active + null-epoch before sending.
    sink.sendPush("audit", { actorEmail: BRIDGE_AUDIT_ACTOR_EMAIL, events });
  }

  function flushSelectionForSettledDocument(): void {
    // Key the re-push decision on the LIVE LOCAL document (NOT the companion's welcome
    // quad belief, which lags: across a soft re-key the quad oscillates stale→fresh, so a
    // belief-keyed reset would re-push twice). The selection always belongs to the live
    // local document; re-assert it under a CHANGED live document exactly once.
    const snap = store.getSnapshot();
    const liveDocumentChanged =
      snap.documentId !== lastPushedSelectionDocumentId ||
      snap.documentEpoch !== lastPushedSelectionDocumentEpoch;
    if (liveDocumentChanged) {
      // The companion's belief for THIS live document carries no selection yet (the live
      // document changed since the last push — a same-membership-across-swap selection
      // would otherwise be swallowed by the membership diff). RESET the selection
      // high-water to a SENTINEL that cannot equal a real membership snapshot, so the
      // `checkSelectionNow` below re-pushes the CURRENT selection under the new document
      // even when the membership is byte-identical (the sentinel differs even from `[]`).
      lastPushedSelection = [RESETTLE_SENTINEL];
    }
    // [@codex E] CLEAR the re-key-pending gate BEFORE the flush: this welcome-driven flush
    // fires AFTER the WS-FIFO `document-changed` re-key frame has already left the socket
    // (the companion has now adopted the swap — the document is SETTLED), so the selection
    // push below is safe to emit. Clearing here (not in `checkDocumentSwapNow`) is what makes
    // the selection land EXACTLY ONCE under the settled document: while the re-key was in
    // flight every interim `checkSelectionNow` was a no-op, so the one pending change flushes
    // here. `checkSelectionNow` reads `reKeyPending`, so it MUST be false before the call.
    reKeyPending = false;
    // Flush the pending selection UNDER THE SETTLED document — exactly once. The call is
    // role/pairing/membership-gated (an observer / unpaired / unchanged tick is a no-op),
    // and on a successful push it advances BOTH `lastPushedSelection` AND the live-document
    // high-water, so a re-key echo welcome (same live document) re-flushes to a no-op. The
    // selection leaves AFTER the WS-FIFO `document-changed` re-key frame → lands under the
    // settled document.
    checkSelectionNow();
  }

  function stop(): void {
    if (stopped) {
      return;
    }
    stopped = true;
    unsubscribe();
  }

  return {
    emitTurnAuditOnCommit,
    checkSelectionNow,
    checkDocumentSwapNow,
    checkDemotionNow: checkDemotion,
    flushSelectionForSettledDocument,
    stop,
  };
}
